import React, { useState } from "react";
import { X, Plus, Upload, Loader2 } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import {
  createPaymentMethod,
  selectCreateStatus,
} from "../../../RTK/Payment-methods/GetAllPaymentMMethods";
import { toast } from "react-toastify";

const CreatePaymentMethodModal = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    name: "",
    provider: "",
    fee_percentage: "",
    minimum_deposit_amount: "",
    currency_price: "",
    payment_method_account_type: "",
    description: "",
    payment_method_info: "",
    is_active: false,
    is_automatic: false,
  });
  const [providerImage, setProviderImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const createStatus = useSelector(selectCreateStatus);
  const isLoading = createStatus === "loading";
  if (!isOpen) return null;

  // In your CreatePaymentMethodModal component
  const handleCreate = async (e) => {
    e.preventDefault();

    try {
      const formDataToSend = new FormData();

      // Append the file first
      if (providerImage) {
        formDataToSend.append("provider_image", providerImage);
      }

      // Append other form fields
      Object.keys(formData).forEach((key) => {
        // Convert boolean values to strings
        if (typeof formData[key] === "boolean") {
          formDataToSend.append(key, formData[key] ? "true" : "false");
        } else {
          // Make sure we don't append empty values
          if (formData[key] !== "" && formData[key] != null) {
            formDataToSend.append(key, formData[key]);
          }
        }
      });

      // Log FormData contents for debugging (optional)
      for (let pair of formDataToSend.entries()) {
        console.log(pair[0] + ": " + pair[1]);
      }

      const result = await dispatch(
        createPaymentMethod(formDataToSend)
      ).unwrap();
      onClose();
    } catch (error) {
      // Handle specific API error messages
      if (error?.provider_image) {
        toast.error(error.provider_image[0]);
      } else if (error?.message) {
        toast.error(error.message);
      } else {
        toast.error("حدث خطأ أثناء إنشاء طريقة الدفع");
      }
      console.error("Failed to create:", error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProviderImage(file);
      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/80 backdrop-blur-md z-50 flex items-center justify-center p-4"
      dir="rtl"
    >
      <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl w-full max-w-2xl">
        <div className="p-6 space-y-6">
          <div className="flex items-center justify-between border-b border-gray-700 pb-6">
            <div className="flex items-center gap-3 text-green-400">
              <Plus className="w-6 h-6" />
              <h3 className="text-xl font-semibold">إضافة طريقة دفع جديدة</h3>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-700 rounded-xl transition-colors"
            >
              <X className="w-6 h-6 text-gray-400" />
            </button>
          </div>

          <form onSubmit={handleCreate} className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <div className="space-y-2 col-span-full">
                <div className="flex flex-col items-center gap-4 p-4 border-2 border-dashed border-gray-700 rounded-xl bg-gray-800/50">
                  <input
                    type="file"
                    id="provider_image"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="hidden"
                  />
                  <label
                    htmlFor="provider_image"
                    className="flex flex-col items-center gap-2 cursor-pointer"
                  >
                    {imagePreview ? (
                      <img
                        src={imagePreview}
                        alt="Preview"
                        className="w-20 h-20 object-contain rounded-full"
                      />
                    ) : (
                      <div className="w-20 h-20 flex items-center justify-center rounded-full bg-gray-700">
                        <Upload className="w-8 h-8 text-gray-400" />
                      </div>
                    )}
                    <span className="text-sm text-gray-400">اختر صورة</span>
                  </label>
                </div>
              </div>

              <div className="space-y-2">
                <input
                  placeholder="اسم طريقة الدفع"
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full bg-gray-800 border border-gray-700 rounded-xl p-3 text-white"
                  required
                />
              </div>

              <div className="space-y-2">
                <input
                  placeholder="مزود الخدمة"
                  type="text"
                  name="provider"
                  value={formData.provider}
                  onChange={handleInputChange}
                  className="w-full bg-gray-800 border border-gray-700 rounded-xl p-3 text-white"
                  required
                />
              </div>

              {/* Replace the input with this select element */}
              <div className="space-y-2">
                <select
                  name="payment_method_account_type"
                  value={formData.payment_method_account_type}
                  onChange={handleInputChange}
                  className="w-full bg-gray-800 border border-gray-700 rounded-xl p-3 text-white appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-gray-600 focus:border-transparent"
                  required
                >
                  <option value="" disabled>
                    نوع الحساب
                  </option>
                  <option value="phone">رقم الهاتف</option>
                  <option value="email">البريد الإلكتروني</option>
                  <option value="account_id">معرف الحساب</option>
                  <option value="token">رمز التحقق</option>
                </select>

              </div>

              <div className="space-y-2">
                <input
                  placeholder="الحد الأدنى للإيداع ($)"
                  type="number"
                  name="minimum_deposit_amount"
                  value={formData.minimum_deposit_amount}
                  onChange={handleInputChange}
                  className="w-full bg-gray-800 border border-gray-700 rounded-xl p-3 text-white"
                  required
                />
              </div>

              <div className="space-y-2">
                <input
                  placeholder="سعر العملة ($)"
                  type="number"
                  name="currency_price"
                  value={formData.currency_price}
                  onChange={handleInputChange}
                  className="w-full bg-gray-800 border border-gray-700 rounded-xl p-3 text-white"
                  required
                />
              </div>

              <div className="space-y-2">
                <input
                  placeholder="نسبة الرسوم (%)"
                  type="number"
                  name="fee_percentage"
                  value={formData.fee_percentage}
                  onChange={handleInputChange}
                  className="w-full bg-gray-800 border border-gray-700 rounded-xl p-3 text-white"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <textarea
                placeholder="الوصف"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                className="w-full bg-gray-800 border border-gray-700 rounded-xl p-3 text-white h-16"
              />
            </div>

            <div className="space-y-2">
              <textarea
                placeholder="معلومات إضافية"
                name="payment_method_info"
                value={formData.payment_method_info}
                onChange={handleInputChange}
                className="w-full bg-gray-800 border border-gray-700 rounded-xl p-3 text-white h-16"
              />
            </div>

            <div className="flex items-center justify-between pt-4">
              {/* Status Toggles */}
              <div className="flex gap-2">
                <label className="relative flex items-center gap-3 p-3 rounded-xl hover:bg-gray-800/50 transition-colors cursor-pointer group">
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleInputChange}
                    className="peer appearance-none w-5 h-5 rounded-lg border-2 border-gray-600 checked:border-green-500 checked:bg-green-500/20 
      transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-green-500/20"
                  />
                  <svg
                    className="absolute w-3 h-3 text-green-500 opacity-0 peer-checked:opacity-100 transition-opacity pointer-events-none left-4"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                  <div className="flex flex-col">
                    <span className="text-sm text-gray-300 group-hover:text-gray-200 transition-colors">
                      نشط
                    </span>
                  </div>
                </label>

                <label className="relative flex items-center gap-3 p-3 rounded-xl hover:bg-gray-800/50 transition-colors cursor-pointer group">
                  <input
                    type="checkbox"
                    name="is_automatic"
                    checked={formData.is_automatic}
                    onChange={handleInputChange}
                    className="peer appearance-none w-5 h-5 rounded-lg border-2 border-gray-600 checked:border-blue-500 checked:bg-blue-500/20 
      transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  />
                  <svg
                    className="absolute w-3 h-3 text-blue-500 opacity-0 peer-checked:opacity-100 transition-opacity pointer-events-none left-4"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                  <div className="flex flex-col">
                    <span className="text-sm text-gray-300 group-hover:text-gray-200 transition-colors">
                      تلقائي
                    </span>
                  </div>
                </label>
              </div>

              <div className="flex gap-2 justify-between ">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-2 py-2 rounded-xl bg-gray-700 text-white hover:bg-gray-600 transition-colors"
                >
                  إلغاء
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="px-2 py-2 rounded-xl bg-green-500 text-white hover:bg-green-600 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      جاري الإضافة...
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4" />
                      إضافة
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreatePaymentMethodModal;
